/**
 * MDX component overrides (Story 30.10). Maps MDX-rendered elements to styled,
 * semantic HTML so blog posts match the site + satisfy the content-guidelines
 * (semantic <table>/<dl>, readable prose). Passed to <MDXProvider>. Custom
 * components (Callout, CTA) and <ToolComparisonTable> (30.12/30.21) live here.
 */
import type { AnchorHTMLAttributes, ReactNode } from "react";

function InternalOrExternalLink({ href, children, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement>) {
  const isInternal = !!href && href.startsWith("/");
  // Internal MDX links may point at ANY route (including ones that ship in a
  // later content story), so a typed <Link> won't compile. A plain same-origin
  // <a> is correct — the speculation rules (Story 30.5) prerender content
  // routes on hover, so navigation still feels instant.
  return (
    <a
      href={href}
      className="text-brand underline underline-offset-2 hover:text-brand/80"
      {...(isInternal ? {} : { target: "_blank", rel: "noopener noreferrer" })}
      {...rest}
    >
      {children}
    </a>
  );
}

/** Callout box for tips/notes inside MDX. */
export function Callout({ children, type = "info" }: { children: ReactNode; type?: "info" | "warning" }) {
  return (
    <div
      className={`my-6 rounded-xl border-[0.5px] border-input p-4 text-[15px] ${
        type === "warning" ? "bg-amber-50" : "bg-muted/40"
      }`}
    >
      {children}
    </div>
  );
}

export const mdxComponents = {
  a: InternalOrExternalLink,
  h2: (props: { children?: ReactNode; id?: string }) => (
    <h2 className="mt-10 mb-3 scroll-mt-24 text-2xl font-semibold tracking-tight text-foreground" {...props} />
  ),
  h3: (props: { children?: ReactNode; id?: string }) => (
    <h3 className="mt-8 mb-2 scroll-mt-24 text-xl font-semibold tracking-tight text-foreground" {...props} />
  ),
  p: (props: { children?: ReactNode }) => (
    <p className="my-5 text-[17px] leading-[1.75] text-foreground/85" {...props} />
  ),
  ul: (props: { children?: ReactNode }) => (
    <ul className="my-5 list-disc space-y-2 pl-6 text-[17px] leading-[1.7] text-foreground/85" {...props} />
  ),
  ol: (props: { children?: ReactNode }) => (
    <ol className="my-5 list-decimal space-y-2 pl-6 text-[17px] leading-[1.7] text-foreground/85" {...props} />
  ),
  // Inline images inside MDX (Story 30.10 / UI pass). Rounded, bordered, lazy.
  // If the asset isn't present yet (image files are provisioned separately),
  // onError hides the element so there's no broken-image icon.
  img: (props: { src?: string; alt?: string }) => (
    <img
      className="my-6 w-full rounded-xl border-[0.5px] border-input"
      loading="lazy"
      onError={(e) => {
        (e.currentTarget as HTMLImageElement).style.display = "none";
      }}
      {...props}
    />
  ),
  hr: () => <hr className="my-10 border-t-[0.5px] border-input" />,
  table: (props: { children?: ReactNode }) => (
    <div className="my-6 overflow-x-auto">
      <table className="w-full border-collapse text-[15px]" {...props} />
    </div>
  ),
  thead: (props: { children?: ReactNode }) => <thead className="bg-muted/50" {...props} />,
  th: (props: { children?: ReactNode }) => (
    <th className="border-[0.5px] border-input px-3 py-2 text-left font-semibold" {...props} />
  ),
  td: (props: { children?: ReactNode }) => (
    <td className="border-[0.5px] border-input px-3 py-2 align-top" {...props} />
  ),
  blockquote: (props: { children?: ReactNode }) => (
    <blockquote className="my-6 rounded-r-lg border-l-[3px] border-input bg-muted/30 py-2 pl-4 pr-3 text-foreground/85" {...props} />
  ),
  code: (props: { children?: ReactNode }) => (
    <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[14px]" {...props} />
  ),
  Callout,
};
